import { useState, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

export const useCitas = () => {
  const [citas, setCitas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [configuracion, setConfiguracion] = useState(null);
  const [listaEspera, setListaEspera] = useState([]);
  const [anticiposPendientes, setAnticiposPendientes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch todos los datos
  const fetchCitas = useCallback(async () => {
    try {
      const [citasRes, clientesRes, serviciosRes] = await Promise.all([
        api.get('/citas/todas'),
        api.get('/clientes'),
        api.get('/servicios')
      ]);

      setCitas(citasRes.data);
      setClientes(clientesRes.data);
      setServicios(serviciosRes.data);

      try {
        const espRes = await api.get('/lista-espera');
        setListaEspera(espRes.data);
      } catch {}

      try {
        const antRes = await api.get('/citas/pendiente-pago');
        setAnticiposPendientes(antRes.data);
      } catch {}

      try {
        const configRes = await api.get('/configuracion/agendamiento');
        setConfiguracion(configRes.data);
      } catch {
        setConfiguracion({ modo_agendamiento: 'dias_fijos', dias_anticipacion_max: 30 });
      }

      return true;
    } catch (error) {
      toast.error('Error al cargar citas');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Crear cita
  const crearCita = useCallback(async (formData) => {
    try {
      const { data } = await api.post('/citas', formData);
      toast.success('Cita creada correctamente');
      return data;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al crear cita');
      throw error;
    }
  }, []);

  // Actualizar estado cita
  const actualizarEstadoCita = useCallback(async (citaId, nuevoEstado) => {
    try {
      const { data } = await api.patch(`/citas/${citaId}/estado`, { estado: nuevoEstado });
      toast.success('Estado actualizado');
      return data;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al actualizar estado');
      throw error;
    }
  }, []);

  // Reagendar cita
  const reagendarCita = useCallback(async (citaId, nuevaFecha, nuevaHora) => {
    try {
      const { data } = await api.patch(`/citas/${citaId}/reagendar`, {
        nueva_fecha: nuevaFecha,
        nueva_hora: nuevaHora
      });
      toast.success('Cita reagendada correctamente');
      return data;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al reagendar');
      throw error;
    }
  }, []);

  // Cancelar cita recurrente (solo esta)
  const cancelarSoloEsta = useCallback(async (citaId) => {
    try {
      const { data } = await api.delete(`/citas/${citaId}`);
      toast.success('Cita cancelada');
      return data;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al cancelar cita');
      throw error;
    }
  }, []);

  // Cancelar todas las citas recurrentes
  const cancelarTodasRecurrentes = useCallback(async (grupoRecurrenciaId) => {
    try {
      const { data } = await api.delete(`/citas/recurrentes/${grupoRecurrenciaId}`);
      toast.success('Serie cancelada');
      return data;
    } catch (error) {
      toast.error('Error al cancelar serie');
      throw error;
    }
  }, []);

  // Actualizar estado lista espera
  const actualizarEsperaEstado = useCallback(async (id, estado) => {
    try {
      await api.patch(`/lista-espera/${id}/estado`, { estado });
      toast.success('Estado actualizado');
    } catch (error) {
      toast.error('Error al actualizar');
      throw error;
    }
  }, []);

  // Eliminar de lista espera
  const eliminarDelEspera = useCallback(async (id) => {
    try {
      await api.delete(`/lista-espera/${id}`);
      toast.success('Eliminado de lista de espera');
    } catch (error) {
      toast.error('Error al eliminar');
      throw error;
    }
  }, []);

  // Confirmar pago anticipo
  const confirmarPago = useCallback(async (id) => {
    try {
      const { data } = await api.patch(`/citas/${id}/confirmar-pago`);
      toast.success('Pago confirmado');
      return data;
    } catch (error) {
      toast.error('Error al confirmar pago');
      throw error;
    }
  }, []);

  // Rechazar pago anticipo
  const rechazarPago = useCallback(async (id) => {
    try {
      const { data } = await api.patch(`/citas/${id}/rechazar-pago`);
      toast.success('Pago rechazado');
      return data;
    } catch (error) {
      toast.error('Error al rechazar pago');
      throw error;
    }
  }, []);

  return {
    // State
    citas,
    clientes,
    servicios,
    configuracion,
    listaEspera,
    anticiposPendientes,
    loading,

    // Setters
    setCitas,
    setClientes,
    setServicios,
    setConfiguracion,
    setListaEspera,
    setAnticiposPendientes,
    setLoading,

    // Methods
    fetchCitas,
    crearCita,
    actualizarEstadoCita,
    reagendarCita,
    cancelarSoloEsta,
    cancelarTodasRecurrentes,
    actualizarEsperaEstado,
    eliminarDelEspera,
    confirmarPago,
    rechazarPago
  };
};
